(() => {
  const root = window.Shopify?.routes?.root || '/';
  const endpoint = (path) => `${root}${path}`.replace(/\/\/{2,}/g, '/');

  const request = async (url, options = {}) => {
    const response = await fetch(endpoint(url), {
      credentials: 'same-origin',
      headers: options.body instanceof FormData ? options.headers : { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    const data = await response.json().catch(() => ({}));
    // 走到兜底说明响应体里没有 description/message —— 最常见是限流/拦截页（430/429，返回 HTML）。
    // 带上状态码，下次一眼能分辨是限流、422 变体问题还是别的。（2026-09-08）
    if (!response.ok) throw new Error(data.description || data.message || `The cart could not be updated. (${response.status}) Please try again.`);
    return data;
  };

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: currency || 'USD',
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  class MzihoCartConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.schema = this.parse('[data-mziho-schema]', {});
      this.variants = this.parse('[data-mziho-variants]', []);
      /**
       * ⚠ Liquid 的 `{{ item.properties | json }}` 吐出来的是
       * **[[key, value], …] 这样的数组**，不是对象。
       * 直接 `{ ...array }` 会得到 `{0: ['k','v'], 1: […]}`，
       * 保存后订单属性变成 `0: "_Configuration ID,mziho-…"` 一串乱码 —— 实际撞到过。
       * 这里统一归一成对象。
       */
      this.properties = this.normalizeProperties(this.parse('[data-mziho-properties]', {}));
      this.currentVariantId = String(this.dataset.variantId || '');
      this.currentVariant = this.variants.find((variant) => String(variant.id) === this.currentVariantId);
      this.saveButton = this.querySelector('[data-mziho-save]');
      this.message = this.querySelector('[data-mziho-message]');
      this.unitPrice = this.querySelector('[data-mziho-unit-price]');
      this.subtotal = this.querySelector('[data-mziho-subtotal]');
      this.currency = this.dataset.currencyCode || 'USD';

      this.addEventListener('change', (event) => {
        this.resetUnpickedEffects(event.target);
        this.clearMessage();
        this.updateConditions();
        this.updatePricePreview();
      });
      this.querySelectorAll('[data-mziho-field]').forEach((field) => {
        field.addEventListener('blur', () => this.validateField(field));
      });
      this.saveButton?.addEventListener('click', () => this.save());
      this.duplicateButton = this.querySelector('[data-mziho-duplicate]');
      this.duplicateButton?.addEventListener('click', () => this.duplicate());
      this.localizeOptionPrices();
      this.restoreEffectPopups();
      this.updateConditions();
      this.updatePricePreview();
    }

    /**
     * 复制当前行：同变体、同数量、同属性，新行拿一个新的 _Configuration ID。
     * 场景是「同款做两个不同图案」——不复制的话客户得把整套配置重走一遍。
     * 图稿文件不能跨行复制（浏览器不允许程序化设置 file input），所以清空，
     * 让客户在新行单独上传。
     */
    async duplicate() {
      const btn = this.duplicateButton;
      if (!btn || btn.disabled) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Duplicating…';
      let addedKey = null;
      try {
        const props = { ...this.properties };
        delete props['_Configuration ID'];
        Object.keys(props).forEach((k) => {
          if (/artwork|upload|file/i.test(k)) delete props[k];   // 文件无法复制
        });
        const newId = `mziho-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        props['_Configuration ID'] = newId;
        // 2026-10-21：本行改了没保存（Unsaved）时表单和已保存属性对不上，按哪边算加价都可能错 ——
        //   复制品不带 complete 标记，新行显示未完成、拦住结账，客人在新行点 Save 时按那行的选择重建加价。
        const saved = this.dataset.complete === 'true';
        if (!saved) delete props['_Configuration status'];
        const quantity = Number(btn.dataset.quantity) || 1;

        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: Number(btn.dataset.variantId),
            quantity,
            properties: props,
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        addedKey = (await res.json().catch(() => ({})))?.key || null;
        /**
         * 2026-10-21：以前只复制主商品行，加价行（product-options）没跟着复制，复制品却带着
         * `_Configuration status = complete` 直接放行结账 —— 实测软珐琅 .75"×25 + Rainbow + Hat Clip 复制一行少收 $114.62。
         * 和 save() 一样：按本行（已保存、未改动）的选择给新行重建加价行，再把新主商品挪到自己的加价行前面。
         */
        if (saved) {
          await this.syncAddons(newId, quantity);
          await this.bringMainToFront(newId).catch((error) => console.warn('[mziho] 复制品排序失败（不影响金额）', error));
        }
        window.location.reload();
      } catch (e) {
        // 加价行没建成：撤掉复制品，别留下一行「Complete 但没收加价」；万一留下半截加价行，下次加载 sweep 会当孤儿清掉
        if (addedKey) await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: addedKey, quantity: 0 }) }).catch(() => {});
        btn.disabled = false;
        btn.textContent = original;
        this.showMessage('Could not duplicate this item. Please try again.');
      }
    }

    normalizeProperties(raw) {
      if (Array.isArray(raw)) {
        const out = {};
        raw.forEach((pair) => {
          // 2026-10-30：Shopify 读出来的属性带 HTML 实体，还原后再用，否则每保存/复制一次多转义一层
          if (Array.isArray(pair) && pair.length >= 1) {
            out[String(pair[0])] = typeof pair[1] === 'string'
              ? pair[1].replace(/&(amp;)+/g, '&').replace(/&quot;/g, '"').replace(/&#0?39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>')
              : pair[1];
          }
        });
        return out;
      }
      return raw && typeof raw === 'object' ? { ...raw } : {};
    }

    parse(selector, fallback) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || ''); }
      catch (_error) { return fallback; }
    }

    selectedVariant() {
      const options = [...(this.currentVariant?.options || [])];
      this.querySelectorAll('[data-field-kind="variant_option"], [data-field-kind="quantity"][data-option-position]:not([data-option-position="0"])').forEach((field) => {
        const position = Number(field.dataset.optionPosition || 0);
        if (!position) return;
        // 2026-09-14：variant_option 可渲染成图片卡片（多个 radio），只认选中的那个
        if (field.type === 'radio' && !field.checked) return;
        const selected = field.options?.[field.selectedIndex];
        options[position - 1] = selected?.dataset.optionValue || field.value;
      });
      return this.variants.find((variant) => variant.options.every((value, index) => String(value) === String(options[index])));
    }

    selectedQuantity() {
      const field = this.querySelector('[data-field-kind="quantity"]');
      const value = Number.parseInt(field?.value || this.dataset.quantity || '1', 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    selectedAddons() {
      return [...this.querySelectorAll('[data-field-kind="addon"]:checked')];
    }

    fieldValue(key) {
      const fields = [...this.querySelectorAll(`[data-mziho-field][data-field-key="${CSS.escape(String(key))}"]`)]
        .filter((field) => !field.disabled);
      if (!fields.length) return '';
      /**
       * ⚠ 勾选框没勾时**不能**退回去拿 `fields[0].value` ——
       * 购物车侧勾选框的 value 是写死的 "Yes"（要写进订单属性给客服看），
       * 退回去拿等于「没勾也返回 Yes」，showIf 条件就永远成立。
       * 这就是「背面上传框不管勾没勾都在」的原因。没勾 = 'false'。
       */
      const checkboxes = fields.filter((field) => field.type === 'checkbox');
      if (checkboxes.length === fields.length) return checkboxes.some((field) => field.checked) ? 'true' : 'false';
      const radios = fields.filter((field) => field.type === 'radio');
      if (radios.length === fields.length) {
        const picked = radios.find((field) => field.checked);
        return picked ? picked.value : '';
      }
      const checked = fields.find((field) => (field.type === 'radio' || field.type === 'checkbox') && field.checked);
      return checked ? checked.value : fields[0].value;
    }

    updateConditions() {
      (this.schema.fields || []).forEach((definition) => {
        const controls = [...this.querySelectorAll(`[data-mziho-field][data-field-key="${CSS.escape(String(definition.key || ''))}"]`)];
        if (!controls.length) return;
        const condition = definition.showIf || definition.show_if;
        /**
         * ⚠ 购物车侧的勾选框 value 是 "Yes"（写进订单属性给客服看的），
         * 而配置里写的是 `equals: true`。直接字符串比较永远不相等，
         * 结果「勾了『背面用不同工艺』但下面什么都不出现」—— 店主实际撞到过。
         * 商品页没这个问题，因为那边 fieldValue 对勾选框特判返回 'true'/'false'。
         * 这里做宽松比较，两种写法都认。
         */
        const truthy = (value) => ['true', 'yes', 'on', '1'].includes(String(value).toLowerCase());
        const raw = condition ? this.fieldValue(condition.field) : '';
        const want = condition ? String(condition.equals) : '';
        const visible = !condition
          || (truthy(want) ? truthy(raw) : String(raw) === want);
        const containers = new Set(controls.map((control) => control.closest('.mziho-cart-configurator__field')).filter(Boolean));
        /**
         * ⚠ 只有真的配了 showIf 才碰 hidden（2026-09-03 修）。
         *   原来无条件执行 container.hidden = !visible，而没有 showIf 的字段
         *   visible 恒为 true，于是每次改动都把 hidden 抹成 false ——
         *   tab 分组面板正是靠 wrap.hidden 隐藏非活动 tab 的，于是三个面板一起展开。
         *   没有条件的字段，可见性归别人管，这里不该插手。
         * ⚠ 如果以后给分组里的字段配了 showIf，两套机制会打架，届时要合并处理。
         */
        if (condition) containers.forEach((container) => { container.hidden = !visible; });
        controls.forEach((control) => {
          if (!control.hasAttribute('data-mziho-required')) control.dataset.mzihoRequired = String(control.required);
          control.disabled = !visible;
          control.required = visible && control.dataset.mzihoRequired === 'true';
        });
      });
      this.applyOptionLimits();
    }

    /**
     * 取值级的「限定其它字段取值」（2026-09-16）——配置里写 limits:{ "Size": ["3\"", ...] }。
     * 场景：PVC 杯垫只做 3"–5"，选中杯垫后尺寸下拉框只留这几档。
     * ⚠ option 同时设 hidden 和 disabled：只设 hidden 在部分浏览器仍可用键盘选到。
     * ⚠ 自动改值会派发 change（让价格和变体跟着变），用 mzLimitBusy 防递归。
     */
    applyOptionLimits() {
      if (this.mzLimitBusy) return;
      const limits = new Map();
      this.querySelectorAll('input[type="radio"][data-option-limits]:checked:not(:disabled)').forEach((el) => {
        let spec = null;
        try { spec = JSON.parse(el.dataset.optionLimits); } catch (_e) { return; }
        if (!spec || typeof spec !== 'object') return;
        for (const [key, allowed] of Object.entries(spec)) {
          if (Array.isArray(allowed) && allowed.length) limits.set(key, allowed.map(String));
        }
      });
      let changed = false;
      this.querySelectorAll('select[data-mziho-field]').forEach((sel) => {
        const allowed = limits.get(sel.dataset.fieldKey);
        [...sel.options].forEach((opt) => {
          const ok = !allowed || allowed.includes(opt.value) || allowed.includes(opt.textContent.trim());
          opt.hidden = !ok;
          opt.disabled = !ok;
        });
        if (!allowed) return;
        const current = sel.options[sel.selectedIndex];
        if (current && !current.disabled) return;
        const usable = [...sel.options].filter((o) => !o.disabled);
        if (!usable.length) return;
        const num = (s) => parseFloat(String(s).replace(/[^\d.]/g, '')) || 0;
        const target = num(current?.value || '');
        const pick = usable.reduce((best, o) => (Math.abs(num(o.value) - target) < Math.abs(num(best.value) - target) ? o : best), usable[0]);
        sel.value = pick.value;
        changed = true;
      });
      if (changed) {
        this.mzLimitBusy = true;
        this.querySelectorAll('select[data-mziho-field]').forEach((sel) => sel.dispatchEvent(new Event('change', { bubbles: true })));
        this.mzLimitBusy = false;
      }
    }

    /**
     * 与商品页 `optionSurcharge()` 等价：带价的单选/图选/下拉，
     * 外加**没有专属商品**的 add-on。返回**元**。
     * 购物车这边原来完全没有这个函数 —— 客户在购物车里选了带价的选项，
     * 页面照样显示加价，实际一分不收。
     */
    /**
     * 一个带价取值该收多少钱（返回**元**）。
     *   'per_unit'（默认）  固定单价 × 数量
     *   'once'              一次性
     *   'percent'           **变体单价**的百分比 × 数量
     *
     * ⚠ percent 的基数是「变体单价」，不含其它选项的加价 ——
     *   对齐 GS-JJ 的口径 "+26% of unit price"；否则多个百分比叠加会滚雪球。
     * ⚠ unitCents 是**分**，price 存的是百分数本身（26 就是 26%）。
     */
    chargeAmount(price, charge, quantity, unitCents) {
      if (charge === 'once') return price * this.fxRate();
      if (charge === 'percent') return (Number(unitCents) / 100) * (price / 100) * quantity;
      return price * this.fxRate() * quantity;
    }

    /**
     * 2026-10-21：美元 → 客人当地币的汇率（与商品页 fxRate() 相同）。
     * 配置里的 price / moldFee / add-on 价都是**美元**；变体价、加价商品单价、购物车都是当地币。
     * 原来直接把美元数当当地币：GTQ 下应加 $2.50 只收了 Q2.48（≈$0.32），GBP/EUR 下反过来多收。
     * USD 恒为 1，行为不变。percent 的基数是变体单价，本来就是当地币，不乘。
     */
    fxRate() {
      if (!this.currency || this.currency === 'USD') return 1;
      const active = window.Shopify?.currency;
      const rate = Number(active?.rate);
      if (active?.active === this.currency && rate > 0) return rate;
      // 兜底：拿不到汇率就用加价商品（$0.01）的当地币单价反推 —— 粗，但绝不能按 1 收
      const unit = Number(this.dataset.surchargeUnit || 0);
      return unit > 0 ? unit : 1;
    }

    /**
     * 2026-10-21：选项卡片上的「+$0.10 ea」「+ Mold fee $40.00」是 Liquid money 过滤器直接格式化的美元数，
     * 非 USD 会显示成「Q0.10 ea」。按 data-* 里的美元价换成当地币重写。
     * 🔴 只改已有文本节点的 nodeValue，不用 textContent（购物车里有监听 childList 的 MutationObserver）；USD 不动。
     */
    localizeOptionPrices() {
      const rate = this.fxRate();
      if (rate === 1) return;
      const fmt = (usd) => money(Math.round(Number(usd) * 100 * rate), this.currency);
      const put = (el, text) => {
        const node = el && [...el.childNodes].find((n) => n.nodeType === 3 && /\d/.test(n.nodeValue));
        if (node && node.nodeValue !== text) node.nodeValue = text;
      };
      this.querySelectorAll('input[data-option-price], input[data-option-mold-fee]').forEach((input) => {
        const label = input.closest('label');
        if (!label) return;
        const p = Number(input.dataset.optionPrice || 0);
        const charge = input.dataset.optionCharge;
        if (p && charge !== 'percent') put(label.querySelector('.mziho-dynamic-option__price, .mziho-addon__price:not(.mziho-addon__moldfee)'), `+${fmt(p)}${charge === 'once' ? '' : ' ea'}`);
        const mold = Number(input.dataset.optionMoldFee || 0);
        if (mold) put(label.querySelector('.mziho-addon__moldfee'), `+ Mold fee ${fmt(mold)}`);
      });
      this.querySelectorAll('input[type="checkbox"][data-addon-price]').forEach((input) => {
        const p = Number(input.dataset.addonPrice || 0);
        if (p) put(input.closest('label')?.querySelector('.mziho-addon__price:not(.mziho-addon__moldfee)'), `${fmt(p)}${input.dataset.addonCharge === 'per_unit' ? ' ea' : ''}`);
      });
      this.querySelectorAll('option[data-option-price]').forEach((opt) => {
        const p = Number(opt.dataset.optionPrice || 0);
        const charge = opt.dataset.optionCharge;
        const node = opt.firstChild;
        if (!p || charge === 'percent' || node?.nodeType !== 3) return;
        const text = node.nodeValue.replace(/\(\+[^()]*\)\s*$/, `(+${fmt(p)}${charge === 'once' ? '' : ' ea'})`);
        if (text !== node.nodeValue) node.nodeValue = text;
      });
    }
    optionSurcharge(quantity) {
      let total = 0;
      const unitCents = Number(this.selectedVariant()?.price || 0);
      this.querySelectorAll('input[type="radio"][data-option-price]:checked:not(:disabled), input[type="radio"][data-option-mold-fee]:checked:not(:disabled)').forEach((el) => {
        const p = Number(el.dataset.optionPrice || 0);
        if (p) total += this.chargeAmount(p, el.dataset.optionCharge, quantity, unitCents);
        // 模具费是一次性的，不乘数量。取值可以只有模具费、没有每件加价。
        total += Number(el.dataset.optionMoldFee || 0) * this.fxRate();
      });
      this.querySelectorAll('select[data-mziho-field]:not(:disabled)').forEach((sel) => {
        const opt = sel.options[sel.selectedIndex];
        const p = Number(opt?.dataset?.optionPrice || 0);
        if (p) total += this.chargeAmount(p, opt.dataset.optionCharge, quantity, unitCents);
      });
      this.selectedAddons().forEach((addon) => {
        if (addon.dataset.addonVariantId) return;   // 有专属商品的走 syncAddons，别重复收
        const p = Number(addon.dataset.addonPrice || 0);
        if (p) total += p * this.fxRate() * (addon.dataset.addonCharge === 'per_unit' ? quantity : 1);
      });
      return total;
    }

    /**
     * hidePrice 字段的收费（元）—— 钥匙扣软珐琅 +11% 这类「照收但不单列」的加价（2026-09-16 店主定）。
     * 不进「Options upgrade」提示，改为并进购物车行的单价 / 小计显示，和商品页的数量单价一致。
     * 一次性（once）的不算：摊不进单价，也不该藏。
     */
    hiddenSurcharge(quantity) {
      let total = 0;
      const unitCents = Number(this.selectedVariant()?.price || 0);
      this.querySelectorAll('input[type="radio"][data-option-hide-price][data-option-price]:checked:not(:disabled)').forEach((el) => {
        if (el.dataset.optionCharge === 'once') return;
        total += this.chargeAmount(Number(el.dataset.optionPrice || 0), el.dataset.optionCharge, quantity, unitCents);
      });
      return total;
    }

    /**
     * 每件加价（分）—— 只算**会影响单件价**的两种：
     *   percent   变体单价的百分比（工艺差价走这个）
     *   per_unit  固定单价
     * `once` 不算：模具费是一次性的，摊不进「每件多少钱」。
     * ⚠ 只看选中且没被禁用的 —— showIf 隐藏的字段控件是 disabled 的，不该计入。
     */
    unitSurchargeCents(unitCents) {
      let add = 0;
      this.querySelectorAll('input[type="radio"][data-option-price]:checked:not(:disabled)').forEach((el) => {
        const p = Number(el.dataset.optionPrice || 0);
        if (!p) return;
        const charge = el.dataset.optionCharge;
        if (charge === 'percent') add += Number(unitCents) * (p / 100);
        else if (charge !== 'once') add += p * 100 * this.fxRate();
      });
      return add;
    }

    /**
     * 一次性费用合计（分）：单选/图选的 moldFee 与 charge:'once' 的价格、下拉 charge:'once'、非按件的加购项。
     * 只用于显示「One-time fees」那一格，让「单价 × 数量 + 一次性费用 = 小计」看得明白。（2026-09-12）
     */
    oneTimeCents() {
      let cents = 0;
      this.querySelectorAll('input[type="radio"]:checked:not(:disabled)').forEach((el) => {
        const p = Number(el.dataset.optionPrice || 0);
        if (p && el.dataset.optionCharge === 'once') cents += p * 100;
        cents += Number(el.dataset.optionMoldFee || 0) * 100;
      });
      this.querySelectorAll('select[data-mziho-field]:not(:disabled)').forEach((sel) => {
        const opt = sel.options[sel.selectedIndex];
        const p = Number(opt?.dataset?.optionPrice || 0);
        if (p && opt.dataset.optionCharge === 'once') cents += p * 100;
      });
      this.selectedAddons().forEach((addon) => {
        const p = Number(addon.dataset.addonPrice || 0);
        if (p && addon.dataset.addonCharge !== 'per_unit') cents += p * 100;
      });
      // 2026-10-21：上面累加的都是配置里的美元价，统一换成当地币
      return Math.round(cents * this.fxRate());
    }

    updatePricePreview() {
      const variant = this.selectedVariant();
      const quantity = this.selectedQuantity();
      const unitPrice = Number(variant?.price || 0);
      // 只算有专属商品的 add-on；没有专属商品的已经在 optionSurcharge 里算过，
      // 两边都算会让小计翻倍（商品页同款处理）。
      const addonTotal = this.selectedAddons()
        .filter((addon) => addon.dataset.addonVariantId)
        .reduce((sum, addon) => {
          const price = Number(addon.dataset.addonPrice || 0) * 100 * this.fxRate();   // 美元 -> 当地币分，与商品页一致
          return sum + price * (addon.dataset.addonCharge === 'per_unit' ? quantity : 1);
        }, 0)
        + this.upgradeSurchargeCents(quantity);   // 2026-10-21：按实收取整（加价商品单价的整数倍），USD 结果不变
      /* ⚠ 单价栏必须含每件加价，否则「显示 $8.77 / 实收 $9.65」——
         显示≠实收，和 2026-09-06 那个负加价 bug 同一类。
         小计那行不用改：它已经把 optionSurcharge 全额算进去了。 */
      if (this.unitPrice) {
        this.unitPrice.textContent = variant?.available
          ? money(unitPrice + this.unitSurchargeCents(unitPrice), this.currency)
          : 'Unavailable';
      }
      if (this.subtotal) this.subtotal.textContent = variant?.available ? money(unitPrice * quantity + addonTotal, this.currency) : 'Unavailable';
      // 2026-09-12：一次性费用单独列一格。单价只含每件加价，「单价 × 数量」和小计差的就是它。
      // ⚠ 用 style.display 不用 hidden：CSS 给 __pricing span 设了 display:grid，会压过 [hidden]。
      const oneTimeWrap = this.querySelector('[data-mziho-onetime]');
      if (oneTimeWrap) {
        const oneTime = variant?.available ? this.oneTimeCents() : 0;
        const oneTimeAmount = oneTimeWrap.querySelector('[data-mziho-onetime-amount]');
        const oneTimeText = money(oneTime, this.currency);
        if (oneTimeAmount && oneTimeAmount.textContent !== oneTimeText) oneTimeAmount.textContent = oneTimeText;
        const oneTimeDisplay = oneTime > 0 ? '' : 'none';
        if (oneTimeWrap.style.display !== oneTimeDisplay) oneTimeWrap.style.display = oneTimeDisplay;
      }
      if (this.saveButton) this.saveButton.disabled = !variant || !variant.available;
      this.syncUpgradeNote(quantity);
      this.syncLineProperties();
      this.syncLineTotals(variant, quantity);
    }

    /**
     * 让购物车行的 PRICE / QUANTITY / TOTAL 三栏跟着配置器走。
     *
     * 口径：只算变体价。购物车主行 TOTAL = 变体价 × 数量，选项加价是单独一行
     * （Product options），由「Options upgrade +$X」那行负责显示。把含加价的小计
     * 写进 TOTAL 会和加价行重复计算。
     *
     * 🔴 数量框只设 value，不派发 change/input —— 派发会被主题当成真的改购物车。
     *   （安全前提：CHECKOUT 按钮不在 form#cart 里，结账不会提交 updates[]。）
     * 🔴 金额只改文本节点的 nodeValue，不用 textContent —— 避免 childList 变化
     *   触发那两个全文档 MutationObserver。
     */
    syncLineTotals(variant, quantity) {
      const row = this.closest('tr');
      const host = row && row.previousElementSibling;
      if (!host || !variant) return;

      const qtyInput = host.querySelector('input[name="updates[]"]');
      if (qtyInput && String(quantity) !== qtyInput.value) qtyInput.value = String(quantity);

      // 2026-09-16：hidePrice 的加价（软珐琅 +11%）并进行单价 / 小计显示，不在「Options upgrade」里单列
      const hidden = Math.round(this.hiddenSurcharge(quantity) * 100);
      const unit = Number(variant.price || 0) + Math.round(hidden / quantity);
      const put = (sel, cents) => {
        const span = host.querySelector(sel + ' .price');
        if (!span) return;
        const node = [...span.childNodes].find((n) => n.nodeType === 3 && /\d/.test(n.nodeValue));
        if (!node) return;
        const next = money(cents, this.currency);
        if (node.nodeValue.trim() !== next) node.nodeValue = next;
      };
      put('.cart-item__price', unit);
      put('.cart-item__totals', Number(variant.price || 0) * quantity + hidden);
    }

    /**
     * 把行项目上那几行属性（Size / Quantity / Style ...）实时同步成当前选择。
     * 它们由 snippets/cart-products.liquid 服务端循环渲染，本身不跟着选项动 ——
     * 客户把压铸改成软珐琅后，行项目还写着 Style: Die Struck，而金额已经变了。
     *
     * 🔴 只改文本节点的 nodeValue，不要用 textContent。
     *   textContent 会移除并重建 dd 的子节点（childList 变化），而购物车里有两个
     *   监听整个文档 childList 的 MutationObserver —— 2026-09-06 主线程死循环就是这么来的。
     * ⚠ 只更新已存在的属性行，不动态创建（创建同样是 childList 变化）。
     * ⚠ dd 里含 <a>（上传的文件链接）时跳过。
     */
    syncLineProperties() {
      const row = this.closest('tr');
      const host = row && row.previousElementSibling;
      if (!host) return;
      let props;
      try { props = this.buildProperties(); } catch (e) { return; }
      host.querySelectorAll('[data-mziho-prop]').forEach((el) => {
        const key = el.dataset.mzihoProp;
        if (!key || key.charAt(0) === '_') return;
        const val = props[key];
        if (val == null) return;
        const dd = el.querySelector('dd');
        if (!dd || dd.querySelector('a')) return;
        const node = [...dd.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim());
        if (!node) return;
        if (node.nodeValue.trim() === String(val)) return;
        node.nodeValue = ' ' + val + ' ';
      });
    }

    /**
     * 与实收一致的「选项升级」总额（分）。
     * 🔴 取整必须和 rebuildSurcharge() 完全一样，否则提示会比实收少一分钱：
     *   硬珐琅 25 件 optionSurcharge = 21.925 元 = 2192.5 分 -> Math.round -> 2193 = $21.93。
     */
    /** 加价行实收（分）：件数取整后 × 加价商品单价，和 rebuildSurcharge() 一致。 */
    upgradeSurchargeCents(quantity) {
      const unitCents = Number(this.dataset.surchargeUnit || 1) || 1;
      const amount = this.optionSurcharge(quantity);
      return amount > 0 ? Math.round((amount * 100) / unitCents) * unitCents : 0;
    }

    upgradeNoteTotalCents(quantity) {
      const surchargeCents = this.upgradeSurchargeCents(quantity);
      // 2026-09-16：隐藏加价已并进行单价，这里扣掉，免得重复显示
      const hiddenCents = Math.min(surchargeCents, Math.round(this.hiddenSurcharge(quantity) * 100));
      const addonCents = this.selectedAddons()
        .filter((addon) => addon.dataset.addonVariantId)
        .reduce((sum, addon) => {
          const price = Number(addon.dataset.addonPrice || 0) * 100 * this.fxRate();
          return sum + price * (addon.dataset.addonCharge === 'per_unit' ? quantity : 1);
        }, 0);
      return surchargeCents - hiddenCents + addonCents;
    }

    /**
     * 把商品行上那条「Options upgrade +$X」实时同步成当前选择的金额。
     * 它由 snippets/cart-products.liquid 服务端渲染，本身不会跟着选项动 ——
     * 客户把硬珐琅改成软珐琅后，提示还挂着 +$21.93。
     * 配置器行紧跟在它对应的商品行之后，所以往上一个 <tr> 里找。
     * ⚠ 元素带 inline style="display:flex"，内联压过 [hidden]{display:none}，
     *   只能改 style.display，不能用 el.hidden。
     */
    syncUpgradeNote(quantity) {
      const row = this.closest('tr');
      const host = row && row.previousElementSibling;
      const note = host && host.querySelector('[data-mziho-upgrade-note]');
      if (!note) return;
      const total = this.upgradeNoteTotalCents(quantity);
      const amount = note.querySelector('[data-mziho-upgrade-amount]');
      if (total > 0) {
        // 2026-09-12：没保存时标「(unsaved)」。这里是当前选择的预览，订单汇总里还是已保存的总价，
        // 两个数挨着却对不上，客户会以为算错了。complete 由服务端给出，改选项时 markDirty 置为 false。
        const unsaved = this.dataset.complete !== 'true';
        const noteText = '+' + money(total, this.currency) + (unsaved ? ' (unsaved)' : '');
        if (amount && amount.textContent !== noteText) amount.textContent = noteText;
        note.style.display = 'flex';
      } else {
        note.style.display = 'none';
      }
    }

    validateField(field) {
      field.removeAttribute('aria-invalid');
      if (!field.checkValidity()) field.setAttribute('aria-invalid', 'true');
      return field.checkValidity();
    }

    validate() {
      const fields = [...this.querySelectorAll('[data-mziho-field]')];
      const invalid = fields.find((field) => !this.validateField(field));
      if (invalid) {
        this.showMessage('Please complete the required option before saving.');
        invalid.focus();
        return false;
      }
      const variant = this.selectedVariant();
      if (!variant || !variant.available) {
        this.showMessage('This option combination is unavailable. Choose another combination.');
        return false;
      }
      return true;
    }

    buildProperties() {
      const properties = { ...this.properties };
      const grouped = new Map();

      this.querySelectorAll('[data-mziho-field]').forEach((field) => {
        const key = field.dataset.fieldKey;
        const kind = field.dataset.fieldKind;
        if (!key || field.disabled || kind === 'file' || ((['radio', 'image_choice', 'addon'].includes(kind) || field.type === 'radio') && !field.checked)) return;
        let value = field.value;
        if (kind === 'checkbox') value = field.checked ? 'Yes' : 'No';
        if (kind === 'quantity') value = field.options?.[field.selectedIndex]?.textContent?.trim() || value;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(value);
      });

      grouped.forEach((values, key) => { properties[key] = values.join(', '); });
      this.writeEffectProperties(properties, grouped);
      properties['_Configuration ID'] = properties['_Configuration ID'] || `mziho-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      properties['_Configuration status'] = 'complete';
      // 下划线开头 = 对客户隐藏。这两个是记账字段，不该显示在购物车行里。
      properties['_Displayed unit price'] = this.unitPrice?.textContent || '';
      properties['_Displayed subtotal'] = this.subtotal?.textContent || '';
      // 清掉旧版遗留的无下划线键，避免同一行同时出现两份
      delete properties['Displayed unit price'];
      delete properties['Displayed subtotal'];
      delete properties['Request type'];
      return properties;
    }

    appendProperties(form, properties) {
      Object.entries(properties).forEach(([key, value]) => {
        if (value != null && typeof value !== 'object') form.append(`properties[${key}]`, String(value));
      });
      this.querySelectorAll('[data-field-kind="file"]').forEach((input) => {
        const file = input.files?.[0];
        if (file) form.set(`properties[${input.dataset.fieldKey}]`, file);
      });
    }

    async changeMainItem(variant, quantity, properties) {
      const changedVariant = String(variant.id) !== this.currentVariantId;
      const hasNewFile = [...this.querySelectorAll('[data-field-kind="file"]')].some((input) => input.files?.length);

      if (!changedVariant) {
        if (hasNewFile) {
          const form = new FormData();
          form.append('id', this.dataset.lineKey);
          form.append('quantity', String(quantity));
          this.appendProperties(form, properties);
          return request('cart/change.js', { method: 'POST', body: form });
        }
        return request('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: this.dataset.lineKey, quantity, properties }),
        });
      }

      const addForm = new FormData();
      addForm.append('id', String(variant.id));
      addForm.append('quantity', String(quantity));
      this.appendProperties(addForm, properties);
      const added = await request('cart/add.js', { method: 'POST', body: addForm });
      try {
        await request('cart/change.js', {
          method: 'POST', body: JSON.stringify({ id: this.dataset.lineKey, quantity: 0 }),
        });
      } catch (error) {
        if (added?.key) {
          await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: added.key, quantity: 0 }) }).catch(() => {});
        }
        throw error;
      }
      return added;
    }

    async syncAddons(groupId, quantity) {
      const cart = await request('cart.js');
      const oldAddons = cart.items.filter((line) => line.properties?.['_Configurator parent'] === groupId);
      for (const line of oldAddons) {
        await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 0 }) });
      }

      for (const addon of this.selectedAddons()) {
        if (!addon.dataset.addonVariantId) continue;
        const addonForm = new FormData();
        addonForm.append('id', addon.dataset.addonVariantId);
        addonForm.append('quantity', addon.dataset.addonCharge === 'per_unit' ? String(quantity) : '1');
        addonForm.append('properties[_Configurator parent]', groupId);
        addonForm.append('properties[Upgrade for]', addon.value);
        await request('cart/add.js', { method: 'POST', body: addonForm });
      }

      /**
       * ⚠ 上面那个循环第一步就把**所有** `_Configurator parent = groupId` 的行删了，
       * 加价行（product-options）正好带着这个标记，所以一起被删。
       * 而重建时只重建有 variantId 的 add-on —— 我们的 add-on 都没有 variantId，
       * 结果「客户在购物车点一下保存，$88 加价当场归零」。实测撞到过。
       * 这里按当前选择重新计算并重建加价行，逻辑与商品页 addSurcharge() 一致。
       */
      await this.rebuildSurcharge(groupId, quantity);
    }

    /** 按当前选择重建 $0.01 计分的加价行。收 $88 就是加 8800 个。 */
    async rebuildSurcharge(groupId, quantity) {
      const variantId = Number(this.dataset.surchargeVariantId || 0);
      const unitCents = Number(this.dataset.surchargeUnit || 1);
      if (!variantId || !(unitCents > 0)) return;
      const amount = this.optionSurcharge(quantity);        // 元
      if (!(amount > 0)) return;
      const units = Math.round((amount * 100) / unitCents);
      if (units < 1) return;

      const form = new FormData();
      form.append('id', String(variantId));
      form.append('quantity', String(units));
      form.append('properties[_Configurator parent]', groupId);
      form.append('properties[Upgrade for]', 'Selected options');
      // 2026-10-21：记下重建时的币种，换币种后 resyncSurchargeCurrency() 据此判断件数过期
      form.append('properties[_Currency]', this.currency);
      await request('cart/add.js', { method: 'POST', body: form });
    }

    /**
     * 2026-10-21：让主商品排在自己的加价行 / 附加品前面（订单和手机推送显示第一行）。
     * syncAddons 重建加价行后，加价行是最后加的，会压到主商品上面。
     * 做法同 changeMainItem 换变体：先按原样加一行新的主商品（多带 _Saved 时间戳，否则会和旧行合并成一行），
     * 再删旧行；删旧行失败就撤掉新行 —— 主商品不会丢，也不会变成两份。
     */
    async bringMainToFront(groupId) {
      const cart = await request('cart.js');
      const items = cart.items || [];
      const mainIndex = items.findIndex((line) => line.properties?.['_Configuration ID'] === groupId);
      if (mainIndex < 0) return;
      const childAbove = items.slice(0, mainIndex).some((line) => line.properties?.['_Configurator parent'] === groupId);
      if (!childAbove) return;
      const old = items[mainIndex];
      const properties = {};
      Object.entries(old.properties || {}).forEach(([key, value]) => { if (value != null && value !== '') properties[key] = value; });
      properties._Saved = String(Date.now());
      const added = await request('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: old.variant_id, quantity: old.quantity, properties }] }),
      });
      const newKey = added?.items?.[0]?.key;
      try {
        await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: old.key, quantity: 0 }) });
      } catch (error) {
        if (newKey) await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: newKey, quantity: 0 }) }).catch(() => {});
        throw error;
      }
    }

    async save() {
      if (!this.validate()) return;
      const variant = this.selectedVariant();
      const quantity = this.selectedQuantity();
      const properties = this.buildProperties();
      this.setLoading(true);
      this.clearMessage();
      try {
        await this.changeMainItem(variant, quantity, properties);
        await this.syncAddons(properties['_Configuration ID'], quantity);
        await this.bringMainToFront(properties['_Configuration ID']);
        this.dataset.complete = 'true';
        this.showMessage('Configuration saved. Updating your cart…', false);
        window.location.reload();
      } catch (error) {
        this.showMessage(error.message || 'The configuration could not be saved. Please try again.');
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.saveButton) return;
      this.saveButton.disabled = loading;
      this.saveButton.setAttribute('aria-busy', String(loading));
      const label = this.saveButton.querySelector('[data-mziho-save-label]');
      const spinner = this.saveButton.querySelector('.mziho-cart-configurator__spinner');
      if (label) label.textContent = loading ? 'Saving…' : 'Save configuration';
      if (spinner) spinner.hidden = !loading;
    }

    showMessage(text, error = true) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.setAttribute('role', error ? 'alert' : 'status');
    }

    clearMessage() { if (this.message) this.message.textContent = ''; }

    /* MZIHO-EFFECT-POPUP 2026-10-30：弹窗填颜色数量 / 要求。价格只改 data-option-price，算价函数不用动 */
    effectPopupConfig(input) {
      try { return JSON.parse(input.dataset.optionPopup || 'null'); } catch (_error) { return null; }
    }

    formatUsd(usd) {
      return money(Math.round(Number(usd) * 100 * this.fxRate()), this.currency);
    }

    /** 确认弹窗：记下颜色数 / 文字，把生效单价改成 基础价 × 颜色数（元，美元口径，和配置一致） */
    applyEffectPopup(input, count, note) {
      if (!input.dataset.optionBasePrice) input.dataset.optionBasePrice = input.dataset.optionPrice || '0';
      const n = count > 0 ? count : 1;
      input.dataset.popupCount = String(count > 0 ? count : '');
      input.dataset.popupNote = note || '';
      input.dataset.optionPrice = (Number(input.dataset.optionBasePrice) * n).toFixed(4);
      this.syncEffectLabel(input);
    }

    /** 卡片上的价格文字。🔴 只改文本节点 nodeValue */
    syncEffectLabel(input) {
      const config = this.effectPopupConfig(input);
      const el = input.closest('label')?.querySelector('.mziho-dynamic-option__price, .mziho-addon__price:not(.mziho-addon__moldfee)');
      const node = el && [...el.childNodes].find((n) => n.nodeType === 3 && /\d/.test(n.nodeValue));
      if (!config || !node || input.dataset.optionCharge === 'percent') return;
      const base = Number(input.dataset.optionBasePrice || input.dataset.optionPrice || 0);
      const count = Number(input.dataset.popupCount || 0);
      const unit = String(config.unit || 'Color').toLowerCase();
      let text = `+${this.formatUsd(base)}${config.count ? ` /${unit}` : ''} ea`;
      if (config.count && count) text = `${count} ${unit}${count > 1 ? 's' : ''}: +${this.formatUsd(base * count)} ea`;
      if (node.nodeValue !== text) node.nodeValue = text;
    }

    /** 同组选了别的取值：没被选中的特效退回基础价、清掉填写内容 */
    resetUnpickedEffects(target) {
      if (!target || target.type !== 'radio' || !target.name) return;
      this.querySelectorAll(`input[type="radio"][data-option-popup][name="${CSS.escape(target.name)}"]`).forEach((input) => {
        if (input.checked || !input.dataset.optionBasePrice) return;
        this.applyEffectPopup(input, 0, '');
      });
    }

    /** 页面加载：卡片价格文字归位，已保存的颜色数恢复进生效单价（否则刷新后加价退回一份） */
    restoreEffectPopups() {
      this.querySelectorAll('input[type="radio"][data-option-popup]').forEach((input) => {
        input.dataset.optionBasePrice = input.dataset.optionPrice || '0';
        const config = this.effectPopupConfig(input);
        if (input.checked) {
          const saved = config?.count ? Number.parseInt(this.properties['Effect quantity'], 10) : 0;
          this.applyEffectPopup(input, saved > 0 ? saved : (config?.count ? 1 : 0), this.properties['Effect details'] || '');
        } else this.syncEffectLabel(input);
      });
    }

    /** 写订单属性。切工艺后隐藏字段的旧特效属性也清掉，不然订单上挂着没选的特效 */
    writeEffectProperties(properties, grouped) {
      delete properties['Effect quantity'];
      delete properties['Effect details'];
      const inputs = [...this.querySelectorAll('input[type="radio"][data-option-popup]')];
      inputs.forEach((input) => {
        const key = input.dataset.fieldKey;
        if (key && !grouped.has(key)) delete properties[key];
      });
      const picked = inputs.find((input) => input.checked && !input.disabled);
      if (!picked) return;
      if (this.effectPopupConfig(picked)?.count && picked.dataset.popupCount) properties['Effect quantity'] = picked.dataset.popupCount;
      if (picked.dataset.popupNote) properties['Effect details'] = picked.dataset.popupNote;
    }
  }

  if (!customElements.get('mziho-cart-configurator')) customElements.define('mziho-cart-configurator', MzihoCartConfigurator);

  const updateCheckoutState = () => {
    const incomplete = document.querySelector('[data-mziho-configurable-item][data-complete="false"]');
    const checkoutButtons = document.querySelectorAll('#checkout, #CartDrawer-Checkout');
    checkoutButtons.forEach((button) => {
      if (incomplete) {
        if (!button.hasAttribute('data-mziho-original-disabled')) {
          button.dataset.mzihoOriginalDisabled = String(button.disabled);
        }
        button.disabled = true;
        button.setAttribute('aria-describedby', 'MzihoCartCheckoutMessage');
      } else {
        if (button.hasAttribute('data-mziho-original-disabled')) {
          button.disabled = button.dataset.mzihoOriginalDisabled === 'true';
          delete button.dataset.mzihoOriginalDisabled;
        }
        button.removeAttribute('aria-describedby');
      }
    });

    const conditions = document.querySelector('[data-purchase-conditions]') || document.querySelector('.drawer__footer');
    let message = document.getElementById('MzihoCartCheckoutMessage');
    if (incomplete && conditions && !message) {
      message = document.createElement('p');
      message.id = 'MzihoCartCheckoutMessage';
      message.className = 'mziho-cart-checkout-message';
      message.setAttribute('role', 'alert');
      conditions.prepend(message);
    } else if (!incomplete && message) {
      message.remove();
      message = null;
    }
    if (incomplete && message) {
      // 「改了没保存」和「从没配过」要给不同的话，否则客户不知道该干嘛。
      // ⚠ 变了才写 —— textContent 是 childList 变化，无条件写会持续触发 observer。
      const next = incomplete.dataset.dirty === 'true'
        ? 'Save your configuration changes before checkout. The order total updates once you save.'
        : 'Complete the required product configuration before checkout. The order total updates once you save.';
      if (message.textContent !== next) message.textContent = next;
    }

    document.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      if (incomplete) {
        if (!container.hasAttribute('data-mziho-original-hidden')) {
          container.dataset.mzihoOriginalHidden = String(container.hidden);
        }
        container.hidden = true;
      } else if (container.hasAttribute('data-mziho-original-hidden')) {
        container.hidden = container.dataset.mzihoOriginalHidden === 'true';
        delete container.dataset.mzihoOriginalHidden;
      }
    });
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest('#checkout, #CartDrawer-Checkout')) return;
    const incomplete = document.querySelector('[data-mziho-configurable-item][data-complete="false"]');
    if (!incomplete) return;
    event.preventDefault();
    incomplete.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    incomplete.querySelector('summary, a, button')?.focus();
  }, true);

  /**
   * 改了选项但没保存 → 视同未配置，重新拦住结账（2026-09-08）。
   * save() 成功会 reload，所以本次加载后的任何改动都是未保存的，不需要 diff。
   * ⚠ 只写属性 —— 下面那个 observer 只监听 childList，属性变化不会触发它。
   */
  const markDirty = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const host = target.closest('mziho-cart-configurator');
    if (!host) return;
    if (host.dataset.complete === 'false') return;   // 已是未完成态，别重复设
    host.dataset.dirty = 'true';
    host.dataset.complete = 'false';
    // 徽章也要跟着改 —— 结账被拦住却顶着绿色 Complete 是自相矛盾的，
    // 和「有模具费却显示 Free」是同一类误导。
    const badge = host.querySelector('.mziho-cart-configurator__badge');
    if (badge && badge.textContent.trim() !== 'Unsaved') {
      badge.classList.remove('is-complete');
      badge.textContent = 'Unsaved';
    }
    updateCheckoutState();
  };
  document.addEventListener('change', markDirty, true);
  document.addEventListener('input', markDirty, true);

  const observer = new MutationObserver(updateCheckoutState);
  const start = () => {
    updateCheckoutState();
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  /**
   * 清理「孤儿加价行」。
   *
   * 加价是靠往购物车塞 N 个 $0.01 的 product-options 实现的（收 $75 就是 7500 个）。
   * 这些行通过 `_Configurator parent` 指向主商品行的 `_Configuration ID`。
   * **Shopify 不做级联删除** —— 客户把主商品行删掉后，加价行会原地留下，
   * 而购物车抽屉又不显示它（客户看到的是空车），
   * 结果就是「空购物车 + 7500 items + $75.00 + 能结账」。店主实际撞到过。
   *
   * 判定为孤儿：
   *   1. 带 `_Configurator parent`，但购物车里已经没有对应 `_Configuration ID` 的主行
   *   2. 是 product-options 却压根没有 parent（只可能是配置器加的，主行没了）
   *
   * 每次页面加载扫一次。删掉后刷新一次页面把抽屉和角标同步过来 ——
   * 用 sessionStorage 做闸，避免万一删不掉时反复刷新。
   */
  const SURCHARGE_HANDLE = 'product-options';
  const SWEEP_FLAG = 'mziho-cart-swept';

  const sweepOrphanSurcharges = async () => {
    let cart;
    try { cart = await request('cart.js'); } catch (_error) { return; }
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      sessionStorage.removeItem(SWEEP_FLAG);
      return;
    }

    const liveGroups = new Set(
      cart.items.map((line) => line.properties && line.properties['_Configuration ID']).filter(Boolean),
    );

    const orphans = cart.items.filter((line) => {
      const parent = line.properties && line.properties['_Configurator parent'];
      if (parent) return !liveGroups.has(parent);
      return line.handle === SURCHARGE_HANDLE;
    });

    if (!orphans.length) { sessionStorage.removeItem(SWEEP_FLAG); return; }
    if (sessionStorage.getItem(SWEEP_FLAG)) return;   // 上一轮已经试过，别刷新循环

    const updates = {};
    orphans.forEach((line) => { updates[line.key] = 0; });
    try {
      sessionStorage.setItem(SWEEP_FLAG, '1');
      await request('cart/update.js', { method: 'POST', body: JSON.stringify({ updates }) });
      window.location.reload();
      return 'reload';
    } catch (error) {
      console.error('[mziho] 清理孤儿加价行失败', error);
    }
  };

  /**
   * 2026-10-21：换币种后加价行件数过期 → 自动按当前币种重建一次。
   *
   * 加价行件数是按加购 / 保存时的币种算的（product-options GTQ 一件 Q0.08、USD 一件 $0.01）。
   * 客人之后切换币种，Shopify 只换单价不改件数：GTQ 存的 11167 件切到 USD 变成 $111.67（应收 $114.62），
   * GBP 存的切到 USD 只收约 75%。以前只有客人点 Save 才会按新币种重建。
   *
   * 加价行带 `_Currency`（addSurcharge / rebuildSurcharge 写入），每次页面加载和 cart.currency 对比：
   *   - 不一致、本页有这个配置的配置器（/cart 页）→ syncAddons + bringMainToFront（= 点 Save 的后半段），刷新一次
   *   - 没有 `_Currency` 的旧行（补丁前加的）：按当前选择重算件数，差 1 件以上才重建
   *   - 本页只有抽屉、没有配置器能重算 → 点结账先去 /cart
   *   - 表单和已保存属性对不上 / 重建失败 / 60 秒内同一行已重建过 → 不自动改，标 Unsaved 拦住结账，让客人点 Save
   * 🔴 只改 dataset，不写 childList（购物车里有监听 childList 的 MutationObserver）；
   *    markDirty 写徽章文本是已有做法，变了才写、只写一次。
   * ⚠ main-cart 和 cart-drawer 两个 section 都引了本脚本，/cart 页可能执行两遍 ——
   *    两份同时重建会各删各加，留下两条加价行（多收一倍），所以用 window 标记只跑一次。
   */
  const FX_RESYNC_FLAG = 'mziho-fx-resync';
  let fxBusy = false;     // 正在重建：先拦住结账，免得按旧件数付款
  let fxGoCart = false;   // 本页修不了（只有抽屉）：结账改去 /cart，在那里重建

  document.addEventListener('click', (event) => {
    if (!fxBusy && !fxGoCart) return;
    if (!event.target.closest('#checkout, #CartDrawer-Checkout')) return;
    event.preventDefault();
    event.stopPropagation();
    if (!fxBusy) window.location.href = endpoint('cart');
  }, true);

  /** 表单当前选择 = 已保存的属性？对不上（如选项改名后表单退回默认值）就不能只重建加价行。 */
  const sameAsSaved = (host) => {
    let built;
    try { built = host.buildProperties(); } catch (_error) { return false; }
    return Object.keys(built).every((key) => key.charAt(0) === '_' || String(built[key]) === String(host.properties[key] ?? ''));
  };

  const resyncSurchargeCurrency = async () => {
    if (window.mzihoFxResyncStarted) return;
    window.mzihoFxResyncStarted = true;
    let cart;
    try { cart = await request('cart.js'); } catch (_error) { return; }
    const currency = cart && cart.currency;
    if (!currency || !Array.isArray(cart.items) || !cart.items.length) return;

    const hosts = new Map();
    document.querySelectorAll('mziho-cart-configurator').forEach((host) => {
      const id = host.properties && host.properties['_Configuration ID'];
      if (id && !hosts.has(id)) hosts.set(id, host);
    });

    const stale = cart.items.filter((line) => {
      const groupId = line.properties && line.properties['_Configurator parent'];
      if (!groupId || line.handle !== SURCHARGE_HANDLE) return false;
      const saved = line.properties._Currency;
      if (saved) return saved !== currency;
      // 补丁前加的旧行不知道币种：只有本页能重算时才比件数
      const host = hosts.get(groupId);
      if (!host || host.dataset.complete !== 'true') return false;
      const unitCents = Number(host.dataset.surchargeUnit || 1) || 1;
      return Math.abs(host.upgradeSurchargeCents(host.selectedQuantity()) / unitCents - line.quantity) > 1;
    });
    // 2026-10-21：已完成、应收加价 > 0，却没有自己的加价行（旧版 Duplicate 复制出来的行）→ 当作过期一起重建。
    //   用主商品行的 key 充当 line.key，下面的 60 秒防循环照样生效。
    hosts.forEach((host, groupId) => {
      if (host.dataset.complete !== 'true') return;
      const unitCents = Number(host.dataset.surchargeUnit || 1) || 1;
      if (host.upgradeSurchargeCents(host.selectedQuantity()) < unitCents) return;
      if (cart.items.some((line) => line.handle === SURCHARGE_HANDLE && line.properties && line.properties['_Configurator parent'] === groupId)) return;
      stale.push({ key: host.dataset.lineKey || groupId, properties: { '_Configurator parent': groupId } });
    });
    if (!stale.length) return;

    let guard = {};
    try { guard = JSON.parse(sessionStorage.getItem(FX_RESYNC_FLAG) || '{}') || {}; } catch (_error) { guard = {}; }
    const todo = [];
    stale.forEach((line) => {
      const groupId = line.properties['_Configurator parent'];
      const host = hosts.get(groupId);
      if (!host) { fxGoCart = true; return; }
      if (host.dataset.complete !== 'true') return;   // 未完成：本来就拦着结账，客人保存时会按当前币种重建
      const last = guard[groupId];
      const looping = last && last.key === line.key && last.to === currency && Date.now() - last.at < 60000;
      if (looping || !sameAsSaved(host)) {
        console.warn('[mziho] 加价行币种过期但不能自动重建，已要求客人保存', groupId, looping ? 'looping' : 'form differs');
        markDirty({ target: host });
        return;
      }
      todo.push({ groupId, host, line });
    });
    if (!todo.length) return;

    fxBusy = true;
    try {
      for (const { groupId, host, line } of todo) {
        guard[groupId] = { key: line.key, to: currency, at: Date.now() };
        try { sessionStorage.setItem(FX_RESYNC_FLAG, JSON.stringify(guard)); } catch (_error) { /* 无痕模式等：靠 _Currency 本身防循环 */ }
        await host.syncAddons(groupId, host.selectedQuantity());
        await host.bringMainToFront(groupId);
      }
      window.location.reload();
    } catch (error) {
      // syncAddons 可能已删掉旧加价行、新行没加上 —— 不刷新，标 Unsaved 拦住结账，客人点 Save 会完整重建
      console.error('[mziho] 换币种后重建加价行失败', error);
      fxBusy = false;
      todo.forEach(({ host }) => markDirty({ target: host }));
    }
  };

  /**
   * 2026-10-22：修复「两行主商品共用一个 _Configuration ID」（商品页连续加购没换编号时加进来的购物车，商品页已修）。
   * 共用编号时 syncAddons 按编号删掉全部子行、只重建自己那行的加价 —— 保存一行就吃掉另一行的加价，两行都 complete 能结账。
   * 做法：除第一行外换新编号，整组（含第一行）改成 pending → 拦住结账，客人逐行点 Save 时各自按自己的选择重建加价。
   * 共用的旧加价行编号没变，归第一行，第一行保存时会被删掉重建。
   * Shopify 的行属性是整组替换，所以带上原有全部属性再改；sessionStorage 闸防反复刷新。
   */
  const DUP_FLAG = 'mziho-dup-id-fixed';
  const fixDuplicateGroupIds = async () => {
    let cart;
    try { cart = await request('cart.js'); } catch (_error) { return; }
    const byId = new Map();
    (cart.items || []).forEach((line) => {
      const id = line.properties && line.properties['_Configuration ID'];
      if (!id || line.properties['_Configurator parent']) return;
      if (!byId.has(id)) byId.set(id, []);
      byId.get(id).push(line);
    });
    const groups = [...byId.values()].filter((lines) => lines.length > 1);
    if (!groups.length) { sessionStorage.removeItem(DUP_FLAG); return; }
    if (sessionStorage.getItem(DUP_FLAG)) return;
    sessionStorage.setItem(DUP_FLAG, '1');
    try {
      for (const lines of groups) {
        for (let i = 0; i < lines.length; i += 1) {
          const properties = {};
          Object.entries(lines[i].properties || {}).forEach(([key, value]) => { if (value != null && value !== '') properties[key] = value; });
          if (i > 0) properties['_Configuration ID'] = `mziho-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          properties['_Configuration status'] = 'pending';
          await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: lines[i].key, quantity: lines[i].quantity, properties }) });
        }
      }
      window.location.reload();
      return 'reload';
    } catch (error) {
      console.error('[mziho] 修复重复配置编号失败', error);
    }
  };

  const bootCartChecks = async () => {
    if ((await fixDuplicateGroupIds()) === 'reload') return;
    if ((await sweepOrphanSurcharges()) === 'reload') return;
    await resyncSurchargeCurrency();
  };

  /**
   * 2026-10-22：主题删改商品（assets/cart.js 删改后派发 cart:updated）不刷新页面，上面加载时那一次清理管不到 ——
   * 删掉主商品后它的加价行还在：页面显示 0 件、总价照旧、能结账；两款删一款会把被删那款的加价一起结账（多收）。
   * 这里补跑一次孤儿清理（有孤儿就删掉并刷新）。先清闸，否则本次会话里清理过一次就再也不跑。
   */
  document.addEventListener('cart:updated', () => {
    sessionStorage.removeItem(SWEEP_FLAG);
    sweepOrphanSurcharges();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCartChecks, { once: true });
  } else { bootCartChecks(); }
})();

/* ---- 触屏端加购区折叠 ----
   ≤900px 生效（手机 + 平板）。脚本不跑 = 保持展开，不会把内容藏死。

   ⚠ 视觉按钮用真正的 <button>，不是给 <legend> 套样式。
      iOS Safari 对 legend 的 CSS 支持有缺陷，加 display/padding 会让文字不渲染，
      表现就是「一个没有文字的按钮」。 */
(function () {
  var mq = window.matchMedia('(max-width: 900px)');

  function ensureButton(el) {
    var btn = el.querySelector(':scope > .mzcol-btn');
    if (btn) return btn;
    var lg = el.querySelector(':scope > legend');
    if (!lg) return null;
    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mzcol-btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.appendChild(document.createTextNode(lg.textContent.trim()));
    var chev = document.createElement('span');
    chev.className = 'mzcol-btn__chev';
    chev.setAttribute('aria-hidden', 'true');
    btn.appendChild(chev);
    lg.insertAdjacentElement('afterend', btn);
    return btn;
  }

  function apply() {
    [].forEach.call(document.querySelectorAll('[data-mziho-collapse]'), function (el) {
      if (mq.matches) {
        var btn = ensureButton(el);
        if (!btn) return;
        el.classList.add('is-collapsible');
        // 已经勾了加购项的默认展开，否则客户看不到自己选了什么
        if (el.querySelector('input[type="checkbox"]:checked')) el.classList.add('is-open');
        btn.setAttribute('aria-expanded', el.classList.contains('is-open') ? 'true' : 'false');
      } else {
        el.classList.remove('is-collapsible', 'is-open');
        var b = el.querySelector(':scope > .mzcol-btn');
        if (b) b.remove();
      }
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.mzcol-btn');
    if (!btn) return;
    var box = btn.closest('[data-mziho-collapse]');
    if (!box || !box.classList.contains('is-collapsible')) return;
    var open = box.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);

  document.addEventListener('DOMContentLoaded', apply);
  // 配置器是 JS 渲染的，DOM 可能晚于 DOMContentLoaded 才出现
  setTimeout(apply, 400);
  setTimeout(apply, 1500);
  setTimeout(apply, 3000);
  apply();
})();

/**
 * 选项分组（2026-09-02 新增）—— 照 GS-JJ 的 STEP 6「Advanced Options」。
 *
 * 配置里给字段加 group / groupLabel，渲染完成后把这些字段的容器搬进
 * 一个带 tab 的面板。故意做成 DOM 后处理而不是改 Liquid ——
 * 字段渲染那段 case 有 160 行、8 个分支，改它风险远大于收益。
 *
 * ⚠ 有些字段容器是 <fieldset>（radio/checkbox 组）。只给外层 div 加样式，
 *   不要给 fieldset 套 flex/grid —— iOS Safari 会渲染失败（记忆里的老坑）。
 * ⚠ 购物车每次重渲染都会重新执行，用 dataset 标记防重复。
 */
(() => {

  const buildGroups = (root) => {
    // ⚠ 不能用 root 级的一次性标记：购物车重渲染时这个自定义元素常常不被替换，
    //   标记还在就会直接 return，新字段全是未分组形态（都可见、legend 没藏）。
    //   改成每个分组各自判断是否已就位（见下面的 allInside），每次重渲染都能自愈。
    if (!root) return;
    let schema = null;
    try {
      schema = JSON.parse(root.querySelector('[data-mziho-schema]')?.textContent || 'null');
    } catch (_e) { return; }
    if (!schema || !Array.isArray(schema.fields)) return;

    // 按出现顺序收集分组
    const order = [];
    const byGroup = new Map();
    for (const f of schema.fields) {
      if (!f || !f.group) continue;
      if (!byGroup.has(f.group)) { byGroup.set(f.group, []); order.push(f.group); }
      byGroup.get(f.group).push(f);
    }
    if (!order.length) return;

    for (const groupName of order) {
      const fields = byGroup.get(groupName);
      // 反查每个字段的容器：控件带 data-field-key，往上找 __field
      const panels = [];
      for (const f of fields) {
        const ctrl = root.querySelector('[data-field-key="' + (window.CSS && CSS.escape ? CSS.escape(f.key) : f.key) + '"]');
        const wrap = ctrl && ctrl.closest('.mziho-cart-configurator__field');
        if (wrap) panels.push({ field: f, wrap });
      }
      if (panels.length < 2) continue;   // 只有一个字段就没必要做 tab

      // 已经在自己的面板里 → 什么都不做（避免重复建 tab 条）
      const existing = root.querySelector('[data-mziho-optgroup="' + groupName + '"]');
      if (existing && panels.every((p) => existing.contains(p.wrap))) continue;
      // 否则说明字段被重渲染出去了 —— 删掉陈旧的空面板，下面重建
      if (existing) existing.remove();

      const box = document.createElement('div');
      box.className = 'mziho-optgroup mziho-cart-configurator__field mziho-cart-configurator__field--wide';
      box.dataset.mzihoOptgroup = groupName;

      const head = document.createElement('div');
      head.className = 'mziho-optgroup__head';
      head.innerHTML = '<strong>' + groupName + '</strong><span>(optional)</span>';
      box.appendChild(head);

      const tabs = document.createElement('div');
      tabs.className = 'mziho-optgroup__tabs';
      tabs.setAttribute('role', 'tablist');
      box.appendChild(tabs);

      panels[0].wrap.parentNode.insertBefore(box, panels[0].wrap);

      // 2026-09-12：同名页签合并成一个（金属钥匙扣的 Special Effects 按工艺拆成 4 个 showIf 字段，只该有一个页签）。
      // 页签显隐改用 data-mz-tab-off 属性，不再写 hidden —— hidden 归 showIf 管；
      // 原来切页签会把 showIf 藏起来的字段又显示出来，两套机制互相覆盖。
      if (!document.getElementById('mz-optgroup-tab-style')) {
        const st = document.createElement('style');
        st.id = 'mz-optgroup-tab-style';
        st.textContent = '.mziho-optgroup__panel[data-mz-tab-off]{display:none!important}';
        document.head.appendChild(st);
      }
      const tabOf = (p) => p.field.groupLabel || p.field.label || p.field.key;
      const tabOrder = [];
      panels.forEach((p) => { if (!tabOrder.includes(tabOf(p))) tabOrder.push(tabOf(p)); });
      const showTab = (active) => {
        tabs.querySelectorAll('.mziho-optgroup__tab').forEach((b) => b.setAttribute('aria-selected', b.dataset.tab === active ? 'true' : 'false'));
        panels.forEach((q) => q.wrap.toggleAttribute('data-mz-tab-off', tabOf(q) !== active));
      };
      /**
       * 2026-09-16：showIf 藏掉字段后，它的页签也要藏。
       * 可见性只读面板的 hidden（那是 showIf 写的），不碰 data-mz-tab-off，两套机制不打架。
       * ⚠ 页签按钮和分组容器都用 style.display —— 主题给 .mziho-cart-configurator__field 设了 display，hidden 压不住。
       */
      const syncTabs = () => {
        let active = null, firstVisible = null;
        tabOrder.forEach((label) => {
          const visible = panels.some((p) => tabOf(p) === label && !p.wrap.hidden);
          const btn = [...tabs.querySelectorAll('.mziho-optgroup__tab')].find((b) => b.dataset.tab === label);
          if (!btn) return;
          btn.style.display = visible ? '' : 'none';
          if (!visible) return;
          if (!firstVisible) firstVisible = label;
          if (btn.getAttribute('aria-selected') === 'true') active = label;
        });
        box.style.display = firstVisible ? '' : 'none';
        if (!active && firstVisible) showTab(firstVisible);
      };
      tabOrder.forEach((label) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mziho-optgroup__tab';
        btn.textContent = label;
        btn.dataset.tab = label;
        btn.setAttribute('role', 'tab');
        btn.addEventListener('click', () => showTab(label));
        tabs.appendChild(btn);
      });

      panels.forEach((p) => {

        p.wrap.classList.add('mziho-optgroup__panel');
        // tab 上已经写了字段名，面板里那个 label/legend 是重复的 —— 藏掉。
        // ⚠ 用 hidden 属性不用 CSS 规则：legend 套 CSS 在 iOS Safari 上会渲染失败。
        const ownLabel = [...p.wrap.children].find((el) => el.tagName === 'LABEL' || el.tagName === 'LEGEND');
        if (ownLabel) ownLabel.hidden = true;
        box.appendChild(p.wrap);
      });
      showTab(tabOrder[0]);
      syncTabs();
      // showIf 由配置器自己的 change 监听处理，排到它之后再同步页签
      root.addEventListener('change', () => setTimeout(syncTabs, 0));
    }
  };

  const runAll = () => {
    document.querySelectorAll('mziho-cart-configurator').forEach(buildGroups);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', runAll);
  else runAll();
  // 购物车抽屉/行重渲染之后再跑一次
  document.addEventListener('cart:refresh', runAll);
  new MutationObserver(runAll).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ── 卡片模式：先显示 4 个 + View More（2026-09-03）──────────────────
   只认 .mziho-cart-configurator__field--cards，其余字段完全不动。
   购物车会整段重渲染，所以用 dataset 防重复 + MutationObserver 兜底，
   与分组面板那段的做法一致。 */
(function () {
  if (window.mzihoMoreBound) return;
  window.mzihoMoreBound = true;
  var SHOW = 4;

  function apply(box) {
    if (!box || box.dataset.mzihoMore === '1') return;
    var cards = Array.prototype.filter.call(box.children, function (el) {
      return el.classList && el.classList.contains('mziho-cart-configurator__choice');
    });
    if (cards.length <= SHOW) { box.dataset.mzihoMore = '1'; return; }
    box.dataset.mzihoMore = '1';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mziho-choices__more';
    box.parentNode.insertBefore(btn, box.nextSibling);

    function render(open) {
      box.classList.toggle('is-collapsed', !open);
      btn.textContent = open ? 'View Less' : 'View More (' + (cards.length - SHOW) + ')';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    /* 选中的取值排在第 5 个之后时直接展开 —— 否则客户看不见自己选了什么 */
    var checkedIndex = cards.findIndex(function (el) {
      var i = el.querySelector('input');
      return i && i.checked;
    });
    render(checkedIndex >= SHOW);

    btn.addEventListener('click', function () {
      render(box.classList.contains('is-collapsed'));
    });
  }

  function scan() {
    document.querySelectorAll('.mziho-cart-configurator__field--cards .mziho-cart-configurator__choices')
      .forEach(apply);
  }

  scan();
  document.addEventListener('DOMContentLoaded', scan);
  new MutationObserver(function () { scan(); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();

/* MZIHO-OPTION-VIDEO:START 2026-10-23 选项卡悬停播放视频（商品页 JS 里有同一段，window 标记只挂一次） */
(function () {
  if (window.__mzihoOptionVideo) return;
  window.__mzihoOptionVideo = true;
  var SEL = '.mziho-option__video';
  var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  function videoOf(card) { return card ? card.querySelector(SEL) : null; }
  function play(v) {
    if (!v.getAttribute('src')) v.setAttribute('src', v.dataset.src);
    v.muted = true;
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  function stop(v) {
    v.pause();
    v.parentNode.classList.remove('is-playing');
    try { if (v.readyState) v.currentTime = 0; } catch (e) {}
  }

  // 媒体事件不冒泡，用捕获：真正出画面了才把视频盖到图上，没出画面前看到的还是原图
  document.addEventListener('playing', function (e) {
    var v = e.target;
    if (v && v.matches && v.matches(SEL) && !v.paused) v.parentNode.classList.add('is-playing');
  }, true);

  if (canHover) {
    var edge = function (e, fn) {
      var card = e.target.closest ? e.target.closest('label') : null;
      var v = videoOf(card);
      if (v && !(e.relatedTarget && card.contains(e.relatedTarget))) fn(v);
    };
    document.addEventListener('mouseover', function (e) { edge(e, play); });
    document.addEventListener('mouseout', function (e) { edge(e, stop); });
  } else {
    // 触屏没有悬停：点选哪张卡就播哪张，同组其它停下
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'radio' || !input.checked || !input.closest) return;
      var v = videoOf(input.closest('label'));
      var group = input.closest('fieldset, .mziho-dynamic-options');
      if (group) group.querySelectorAll(SEL).forEach(function (o) { if (o !== v) stop(o); });
      if (v) play(v);
    });
  }
})();
/* MZIHO-OPTION-VIDEO:END */

/* MZIHO-EFFECT-POPUP:START 2026-10-30 特效卡点开弹窗。点卡片先不选中，Confirm 才选中并派发 change；Cancel/Esc/点遮罩什么都不变 */
(function () {
  if (window.__mzihoEffectPopup) return;
  window.__mzihoEffectPopup = true;
  var PRESETS = [1, 2, 3, 4, 5];
  var MAX_DEFAULT = 20;
  var dlg = null;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function close() {
    if (!dlg) return;
    var v = dlg.querySelector('video');
    if (v) { v.pause(); v.removeAttribute('src'); v.load(); }
    if (dlg.open) dlg.close();
    dlg.remove();
    dlg = null;
  }

  function open(input) {
    var host = input.closest('mziho-cart-configurator');
    var config = host && host.effectPopupConfig(input);
    if (!config) return;
    close();
    var label = input.closest('label');
    var video = label.querySelector('video[data-src]');
    var img = label.querySelector('img');
    var base = Number(input.dataset.optionBasePrice || input.dataset.optionPrice || 0);
    var qty = host.selectedQuantity();
    var MAX = config.max || MAX_DEFAULT;
    var unit = config.unit || 'Color';
    var picked = input.checked ? Number(input.dataset.popupCount || 0) : 0;
    var count = picked || 1;

    dlg = el('dialog', 'mz-fxdlg');
    dlg.setAttribute('aria-label', input.value);
    var x = el('button', 'mz-fxdlg__close', '×');
    x.type = 'button';
    x.setAttribute('aria-label', 'Close');
    dlg.appendChild(x);

    var media = el('div', 'mz-fxdlg__media');
    if (video) {
      var v = el('video');
      v.muted = true; v.loop = true; v.autoplay = true; v.playsInline = true;
      v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
      if (img) v.poster = img.currentSrc || img.src;
      v.src = video.dataset.src;
      media.appendChild(v);
    } else if (img) {
      var i = el('img'); i.src = img.currentSrc || img.src; i.alt = '';
      media.appendChild(i);
    }
    dlg.appendChild(media);

    var body = el('div', 'mz-fxdlg__body');
    body.appendChild(el('h3', 'mz-fxdlg__title', input.value));

    var tiles = [];
    var custom = null;
    var total = el('p', 'mz-fxdlg__total');
    var error = el('p', 'mz-fxdlg__error');
    error.setAttribute('role', 'alert');

    function current() {
      if (!config.count) return 0;
      if (custom && custom.closest('.mz-fxdlg__tile').classList.contains('is-on')) {
        var n = Number.parseInt(custom.value, 10);
        return n >= 1 && n <= MAX ? n : -1;
      }
      return count;
    }
    function refresh() {
      var n = current();
      var each = base * (config.count ? Math.max(n, 0) : 1);
      var mold = Number(input.dataset.optionMoldFee || 0);
      var moldText = mold ? '+ ' + host.formatUsd(mold) + ' one-time mold fee' : '';
      if (config.count && n < 1) total.textContent = '';
      else if (input.dataset.optionCharge === 'percent') {
        var unitLocal = Number(host.selectedVariant()?.price || 0) / 100;
        var pctTotal = unitLocal * (base / 100) * qty / host.fxRate();
        total.textContent = '+' + base + '% of unit price = +' + host.formatUsd(pctTotal) + (moldText ? ' ' + moldText : '');
      }
      else if (each > 0) total.textContent = '+' + host.formatUsd(each) + ' ea × ' + qty + ' pcs = +' + host.formatUsd(each * qty) + (moldText ? ' ' + moldText : '');
      else total.textContent = moldText;
      if (custom) {
        var c = Number.parseInt(custom.value, 10);
        custom.closest('.mz-fxdlg__tile').querySelector('.mz-fxdlg__tile-price').textContent =
          c >= 1 && c <= MAX ? '+' + host.formatUsd(base * c) + ' ea' : (c > MAX ? 'Max ' + MAX : 'Enter qty');
        error.textContent = custom.closest('.mz-fxdlg__tile').classList.contains('is-on') && c > MAX
          ? 'You can order up to ' + MAX + ' ' + unit.toLowerCase() + 's per piece here. Need more? Contact us and we will quote it for you.'
          : '';
      }
    }
    function pick(tile, n) {
      tiles.forEach(function (t) { t.classList.toggle('is-on', t === tile); t.setAttribute('aria-pressed', t === tile ? 'true' : 'false'); });
      if (n) count = n;
      error.textContent = '';
      refresh();
    }

    if (config.count) {
      if (config.countLabel) body.appendChild(el('p', 'mz-fxdlg__label', config.countLabel));
      var grid = el('div', 'mz-fxdlg__tiles');
      var usePreset = PRESETS.indexOf(count) > -1;
      PRESETS.forEach(function (n) {
        var t = el('button', 'mz-fxdlg__tile');
        t.type = 'button';
        t.appendChild(el('span', 'mz-fxdlg__tile-name', n + ' ' + unit + (n > 1 ? 's' : '')));
        t.appendChild(el('span', 'mz-fxdlg__tile-price', '+' + host.formatUsd(base * n) + ' ea'));
        t.addEventListener('click', function () { pick(t, n); });
        tiles.push(t);
        grid.appendChild(t);
      });
      var ct = el('label', 'mz-fxdlg__tile mz-fxdlg__tile--custom');
      custom = el('input');
      custom.type = 'number'; custom.min = '1'; custom.max = String(MAX); custom.step = '1';
      custom.inputMode = 'numeric'; custom.placeholder = 'Other';
      custom.setAttribute('aria-label', 'Other quantity');
      if (!usePreset) custom.value = String(count);
      ct.appendChild(custom);
      ct.appendChild(el('span', 'mz-fxdlg__tile-price', 'Enter qty'));
      custom.addEventListener('focus', function () { pick(ct); });
      custom.addEventListener('input', function () { pick(ct); });
      tiles.push(ct);
      grid.appendChild(ct);
      body.appendChild(grid);
      pick(usePreset ? tiles[count - 1] : ct, usePreset ? count : 0);
    } else {
      refresh();
    }

    var note = null;
    if (config.note !== false) {
      var noteId = 'mz-fxdlg-note';
      var noteLabel = el('label', 'mz-fxdlg__label', config.noteLabel || 'Anything we should know?');
      noteLabel.htmlFor = noteId;
      body.appendChild(noteLabel);
      note = el('textarea', 'mz-fxdlg__note');
      note.id = noteId; note.rows = 2; note.maxLength = 200;
      if (config.placeholder) note.placeholder = config.placeholder;
      note.value = input.checked ? (input.dataset.popupNote || '') : '';
      body.appendChild(note);
    }
    body.appendChild(total);
    body.appendChild(error);

    var actions = el('div', 'mz-fxdlg__actions');
    var ok = el('button', 'mz-fxdlg__ok', 'Confirm');
    var no = el('button', 'mz-fxdlg__cancel', 'Cancel');
    ok.type = 'button'; no.type = 'button';
    actions.appendChild(ok);
    actions.appendChild(no);
    body.appendChild(actions);
    dlg.appendChild(body);

    x.addEventListener('click', close);
    no.addEventListener('click', close);
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); });
    ok.addEventListener('click', function () {
      var n = current();
      if (config.count && n < 1) { error.textContent = 'Please enter a number from 1 to ' + MAX + '.'; return; }
      var text = note ? note.value.trim() : '';
      var changed = !input.checked || Number(input.dataset.popupCount || 0) !== n || (input.dataset.popupNote || '') !== text;
      host.applyEffectPopup(input, n, text);
      input.checked = true;
      close();
      if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    document.body.appendChild(dlg);
    dlg.showModal();
    var pv = dlg.querySelector('video');
    if (pv) { var p = pv.play(); if (p && p.catch) p.catch(function () {}); }
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var label = t.closest('label.mziho-cart-configurator__choice');
    if (!label) return;
    var input = label.querySelector('input[type="radio"][data-option-popup]');
    if (!input || input.disabled) return;
    if (t.closest('button')) return;   // 放大、ⓘ 按钮照常工作
    e.preventDefault();
    open(input);
  }, true);
})();
/* MZIHO-EFFECT-POPUP:END */
